import React, { Component } from 'react';
import { Meteor } from 'meteor/meteor';
import { Bert } from 'meteor/themeteorchef:bert';
import { InputGroup, Button, Row, Col, Modal } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { get, isEqual, isEmpty, cloneDeep, findIndex, max, last, filter, forEach } from 'lodash';
import turf from '@turf/turf';

import * as videoOps from '../../../api/Videos/video-operations';
import { YOUTUBE_URL_REGEX } from '../../../api/Videos/Videos';

import LatLon from '../../../modules/GeoHelper';
import { pointsCollectionFeatureToLineString } from '../../../modules/geojson-utilities';
import {
  densifyLine,
  syncLineAtControlPoint,
  removeControlPoint,
  adaptLineStringTimes,
  adaptFeatureArrayTimes
} from '../../../modules/sync-video-line';

import ColBreak from '../../atoms/ColBreak';
import ConfirmationModal from '../../atoms/ConfirmationModal';
import CompanyLogo from '../../atoms/CompanyLogo';
import ResponsivePlayer from '../ResponsivePlayer';
import Slider from '../Slider';
import VideoSpeedControls from '../VideoSpeedControls';
import MapContainerVideoSynchronizer from '../../containers/MapContainerVideoSynchronizer';

import Styles from './styles';

let setYouTubeAPIVideoControls;

const ATTRIBUTES = {
  preload: 'auto'
};
const DISPLAY_BLOCK = 'block';
const DISPLAY_NONE = 'none';
const MAX_NUMBER_OF_POINTS = 2400;
const PLAYBACK_QUALITY = 'hd1080';
const PLAYER_VARS = {
  autoplay: 0,
  controls: 0,
  disablekb: 1,
  enablejsapi: 1,
  fs: 0,
  iv_load_policy: 3,
  loop: 1,
  modestbranding: 1,
  rel: 0
};
const TIME_TOLERANCE = 5;

class VideoSynchronizer extends Component {
  nonClickableDiv = {};

  player = {};

  speedSelect = {};

  timeFlow = {};

  state = {
    bearing: 0,
    clientModulesReady: false,
    currentRPAPosition: this.props.featureArray[0],
    denseFeatureArray: this.props.featureArray,
    denseTrack: videoOps.getTrack(this.props.updatedVideo),
    isClickableDivHidden: false,
    isCSVWarning: false,
    isVideoLoaded: false,
    refreshRate: 1000,
    selectedControlPoint: {},
    showModal: false,
    speed: 1,
    speedArray: [1],
    updateMapLocation: false,
    updateVideo: false,
    videoDuration: 120000000,
    videoTime: 0
  };

  async componentDidMount() {
    const { clientModulesReady } = this.state;
    if (!clientModulesReady) await this.loadClientModules();
  }

  componentDidUpdate(prevProps, prevState) {
    const {
      clientModulesReady,
      denseFeatureArray,
      isVideoLoaded,
      selectedControlPoint,
      speed,
      updateVideo,
      videoDuration,
      videoTime
    } = this.state;
    const {
      denseFeatureArray: prevDenseFeatureArray,
      selectedControlPoint: prevSelectedControlPoint,
      speed: prevSpeed,
      videoDuration: prevVideoDuration,
      videoTime: prevVideoTime
    } = prevState;

    if (clientModulesReady) {
      if (isVideoLoaded) {
        if (updateVideo) {
          this.player.seekTo(videoTime * 1e-6);
        }
        if (speed === 0) {
          this.player.pauseVideo();
        } else {
          this.player.setPlaybackRate(speed);
          this.player.setPlaybackQuality(PLAYBACK_QUALITY);
          this.player.playVideo();
        }
      }

      if (!isEqual(denseFeatureArray, prevDenseFeatureArray)) {
        this.handleVideoTimeChange(videoTime);
      }

      if (!isEqual(videoDuration, prevVideoDuration)) {
        this.handleVideoDurationChange(videoDuration);
      }

      if (!isEqual(selectedControlPoint, prevSelectedControlPoint)) {
        this.handleSelectedControlPointChange(selectedControlPoint);
      }

      if (!isEqual(prevSpeed, speed) && speed !== 0 && !updateVideo) {
        this.changeVideoSynchronizerStateProperty('selectedControlPoint', {});
      }

      if (Math.abs(prevVideoTime - videoTime) > TIME_TOLERANCE) {
        this.handleVideoTimeChange(videoTime);
      }
    }
  }

  componentWillUnmount() {
    clearInterval(this.timeFlow);
    this.nonClickableDiv = null;
    this.player = null;
    this.speedSelect = null;
    this.timeFlow = null;
  }

  changeVideoSynchronizerStateProperty = (property, newValue) =>
    this.setState({ [property]: newValue });

  deleteAllControlPoint = () => {
    const {
      updateVideoProperty,
      changeVideoEditorContainerStateProperty,
      updatedVideo
    } = this.props;
    const videoDuration = videoOps.getDefaultDurationVideo(updatedVideo);

    const refreshRate = 1000;
    let newRefreshRate = refreshRate;

    const pointsInTrack = videoDuration / (refreshRate * 1000);
    if (pointsInTrack > MAX_NUMBER_OF_POINTS) {
      newRefreshRate *= pointsInTrack / MAX_NUMBER_OF_POINTS;
    }

    const newFeatureArray = adaptLineStringTimes({
      line: videoOps.getTrack(updatedVideo),
      totalTime: videoDuration
    });

    const newAllFeature = densifyLine({
      featureArray: newFeatureArray,
      refreshRate: newRefreshRate
    });

    const newControlPoints = turf.featureCollection(
      filter(newAllFeature, ['properties.isControlPoint', true])
    );

    this.setState({
      denseFeatureArray: newAllFeature,
      selectedControlPoint: {}
    });

    changeVideoEditorContainerStateProperty('featureArray', newAllFeature);
    updateVideoProperty('controlPoints', newControlPoints);

    Bert.alert('All Control point removed', 'success');
  };

  deleteControlPoint = () => {
    const { updateVideoProperty, changeVideoEditorContainerStateProperty } = this.props;
    const { denseFeatureArray, selectedControlPoint } = this.state;

    const newDenseFeatureArray = removeControlPoint({
      featureArray: denseFeatureArray,
      selectedControlPoint
    });

    const newControlPoints = turf.featureCollection(
      filter(newDenseFeatureArray, ['properties.isControlPoint', true])
    );

    this.setState({ denseFeatureArray: newDenseFeatureArray, selectedControlPoint: {} });

    changeVideoEditorContainerStateProperty('featureArray', newDenseFeatureArray);
    updateVideoProperty('controlPoints', newControlPoints);

    Bert.alert('Control point removed', 'success');
  };

  getDensifiedTrack = videoDuration => {
    const { updatedVideo } = this.props;
    const { refreshRate } = this.state;

    let newRefreshRate = refreshRate;
    const pointsInTrack = videoDuration / (newRefreshRate * 1000);
    if (pointsInTrack > MAX_NUMBER_OF_POINTS) {
      newRefreshRate *= pointsInTrack / MAX_NUMBER_OF_POINTS;
    }

    const newFeatureArray = adaptLineStringTimes({
      line: videoOps.getTrack(updatedVideo),
      totalTime: videoDuration
    });
    const denseFeatureArray = densifyLine({
      featureArray: newFeatureArray,
      refreshRate: newRefreshRate
    });
    const denseTrack = pointsCollectionFeatureToLineString(
      turf.featureCollection(denseFeatureArray)
    );

    this.setState({
      denseTrack,
      refreshRate: newRefreshRate,
      showModal: false
    });

    return denseFeatureArray;
  };

  getVideoTime = () => {
    const { updateVideo, isVideoLoaded } = this.state;
    if (isVideoLoaded && !updateVideo)
      this.setState({ videoTime: this.player.getCurrentTime() * 1e6 });
  };

  handleClickOnMap = event => {
    const { updatedVideo, ignoreCSVTimes } = this.props;
    const { clientModulesReady, isClickableDivHidden, speed } = this.state;
    if (speed === 0 && clientModulesReady && !isClickableDivHidden) {
      if (
        videoOps.areCoordinatesFromLine(updatedVideo) ||
        videoOps.areCoordinatesFromKML(updatedVideo)
      ) {
        this.setControlPoint(event);
      } else if (videoOps.areCoordinatesFromCSV(updatedVideo)) {
        if (ignoreCSVTimes) {
          this.setControlPoint(event);
        } else if (speed === 0) {
          this.setState({ showModal: true, isCSVWarning: true });
        }
      }
    }
  };

  handleCloseModal = () => this.setState({ showModal: false, isCSVWarning: false });

  handleConfirm = () => {
    const { changeVideoEditorContainerStateProperty, updateVideoProperty } = this.props;
    const { denseFeatureArray } = this.state;

    const newVideoDuration = this.player.getDuration() * 1e6; // Duration of video in microseconds
    const newDenseFeatureArray = adaptFeatureArrayTimes({
      featureArray: denseFeatureArray,
      totalTime: newVideoDuration
    });
    const newControlPoints = turf.featureCollection(
      filter(newDenseFeatureArray, ['properties.isControlPoint', true])
    );

    changeVideoEditorContainerStateProperty('ignoreCSVTimes', true);
    changeVideoEditorContainerStateProperty('featureArray', newDenseFeatureArray);
    updateVideoProperty('controlPoints', newControlPoints);

    this.setState({
      denseFeatureArray: newDenseFeatureArray,
      isCSVWarning: false,
      showModal: false
    });
  };

  handleSelectedControlPointChange = selectedControlPoint => {
    const { speed } = this.state;
    const unselectControlPoint = isEmpty(selectedControlPoint);

    this.setState({
      speed: unselectControlPoint ? speed : 0,
      updateMapLocation: !unselectControlPoint,
      updateVideo: false
    });
  };

  handleVideoDurationChange = videoDuration => {
    const {
      updatedVideo,
      ignoreCSVTimes,
      isTrackUpdated,
      changeVideoEditorContainerStateProperty,
      updateVideoProperty
    } = this.props;
    const { denseFeatureArray } = this.state;
    const lengthControlPointsVideo = isEmpty(updatedVideo.controlPoints)
      ? 0
      : updatedVideo.controlPoints.features.length;
    if (isTrackUpdated) {
      let newDenseFeatureArray = cloneDeep(denseFeatureArray);

      switch (true) {
        case videoOps.areCoordinatesFromLine(updatedVideo):
        case videoOps.areCoordinatesFromKML(updatedVideo):
          this.setState({ showModal: true });
          newDenseFeatureArray = this.getDensifiedTrack(videoDuration);
          break;

        case videoOps.areCoordinatesFromCSV(updatedVideo) && ignoreCSVTimes:
        case videoOps.areCoordinatesFromGoProCSV(updatedVideo) && ignoreCSVTimes:
          newDenseFeatureArray = adaptFeatureArrayTimes({
            featureArray: denseFeatureArray,
            totalTime: videoDuration
          });
          break;

        default:
          break;
      }
      const newControlPoints = turf.featureCollection(
        filter(newDenseFeatureArray, ['properties.isControlPoint', true])
      );

      if (lengthControlPointsVideo <= 2) updateVideoProperty('controlPoints', newControlPoints);
      else updateVideoProperty('controlPoints', updatedVideo.controlPoints);

      changeVideoEditorContainerStateProperty('featureArray', newDenseFeatureArray);
      changeVideoEditorContainerStateProperty('isTrackUpdated', false);

      this.setState({ denseFeatureArray: newDenseFeatureArray });
    }
  };

  handleVideoTimeChange = videoTime => {
    const { denseFeatureArray } = this.state;

    let currentIndex = findIndex(
      denseFeatureArray,
      feature => get(feature, 'properties.timeUS') >= videoTime
    );
    if (currentIndex < 0 || denseFeatureArray.length === 1) {
      currentIndex = 0;
    }
    const currentRPAPosition = denseFeatureArray[currentIndex];

    let currentPoint = {};
    let nextPoint = {};
    let bearing = 0;
    if (currentIndex < denseFeatureArray.length - 2) {
      currentPoint = turf.getCoord(currentRPAPosition);
      nextPoint = turf.getCoord(denseFeatureArray[currentIndex + 1]);
    } else {
      currentPoint = turf.getCoord(denseFeatureArray[currentIndex - 1]);
      nextPoint = turf.getCoord(currentRPAPosition);
    }
    const p1 = new LatLon(currentPoint[1], currentPoint[0]);
    const p2 = new LatLon(nextPoint[1], nextPoint[0]);

    bearing = p1.bearingTo(p2);
    if (bearing < -180) {
      bearing += 360;
    }
    if (bearing > 180) {
      bearing -= 360;
    }

    this.setState({ bearing, currentRPAPosition });
  };

  hideShowClickableDiv = () => {
    const { isClickableDivHidden } = this.state;
    if (isClickableDivHidden) {
      this.nonClickableDiv.style.display = DISPLAY_BLOCK;
    } else {
      this.nonClickableDiv.style.display = DISPLAY_NONE;
    }
    this.setState({ isClickableDivHidden: this.nonClickableDiv.style.display === DISPLAY_NONE });
  };

  initializeLoop = () => {
    const { refreshRate } = this.state;
    this.timeFlow = setInterval(this.getVideoTime, refreshRate);
  };

  onVideoReady = event => {
    const { denseFeatureArray, isVideoLoaded, speed } = this.state;

    if (!isVideoLoaded) this.player = event.getInternalPlayer();

    if (this.player.nodeType === 1) setYouTubeAPIVideoControls(this.player);

    this.player.mute();
    this.player.playVideo();
    this.player.setPlaybackRate(speed);
    this.player.setPlaybackQuality(PLAYBACK_QUALITY);

    const videoDuration = this.player.getDuration() * 1e6; // Duration of video in microseconds
    const speedArray = this.player.getAvailablePlaybackRates();
    this.speedSelect.value = 1;

    const lastFeaturePointTime = get(last(denseFeatureArray), `properties.timeUS`);

    this.setState({
      videoDuration: max([videoDuration, lastFeaturePointTime]),
      isVideoLoaded: true,
      speedArray
    });
  };

  playPause = () => {
    const { speed } = this.state;
    if (speed !== 0) {
      this.setState({ speed: 0 });
    } else {
      this.setState({ speed: Number(this.speedSelect.value) });
    }
  };

  setControlPoint = event => {
    const {
      updatedVideo,
      updateVideoProperty,
      changeVideoEditorContainerStateProperty
    } = this.props;
    const { denseFeatureArray, videoTime } = this.state;

    let isTimeOccupied = false;
    forEach(videoOps.getControlPoints(updatedVideo), controlPointToCheck => {
      const controlPointTime = get(controlPointToCheck, 'properties.timeUS');
      if (Math.abs(controlPointTime - videoTime) < TIME_TOLERANCE) isTimeOccupied = true;
    });

    if (!isTimeOccupied) {
      const { updatedFeatureArray: newDenseFeatureArray, newControlPoint } = syncLineAtControlPoint(
        {
          featureArray: denseFeatureArray,
          latLngPoint: event,
          videoTime
        }
      );
      const newControlPoints = turf.featureCollection(
        filter(newDenseFeatureArray, ['properties.isControlPoint', true])
      );

      this.setState({
        denseFeatureArray: newDenseFeatureArray,
        selectedControlPoint: newControlPoint,
        updateMapLocation: true
      });

      changeVideoEditorContainerStateProperty('featureArray', newDenseFeatureArray);
      updateVideoProperty('controlPoints', newControlPoints);

      Bert.alert('Point synced', 'success');
    } else {
      Bert.alert('Control point already at this time', 'danger');
    }
  };

  setReferenceInVideoSynchronizer = (variableKey, elementNode) => {
    this[variableKey] = elementNode;
  };

  async loadClientModules() {
    if (Meteor.isClient) {
      ({ setYouTubeAPIVideoControls } = await import('../../../modules/geomotionvideo-operations'));
      this.setState({ clientModulesReady: true });
    }
  }

  render() {
    const { updatedVideo, ...rest } = this.props;
    const {
      bearing,
      currentRPAPosition,
      denseTrack,
      isClickableDivHidden,
      isCSVWarning,
      loading,
      selectedControlPoint,
      showModal,
      speed,
      speedArray,
      updateMapLocation,
      videoDuration,
      videoTime
    } = this.state;

    const mapProps = {
      currentRPAPosition,
      rpaBearing: bearing,
      track: denseTrack,
      updatedVideo,
      updateMapLocation,
      changeVideoContainerStateProperty: this.changeVideoSynchronizerStateProperty,
      initializeLoop: this.initializeLoop,
      onClickOnMap: this.handleClickOnMap,
      ...rest
    };

    const sliderProps = {
      controlPoints: videoOps.getControlPoints(updatedVideo).features,
      domain: videoDuration,
      isVideoSynchronizer: true,
      videoTime,
      changeVideoContainerStateProperty: this.changeVideoSynchronizerStateProperty
    };

    const showUnlockButton = videoOps.getURL(updatedVideo).match(YOUTUBE_URL_REGEX);

    return (
      <div className="p-0 w-100">
        {isCSVWarning && (
          <ConfirmationModal
            message="This will delete the times from the CSV file you chose"
            showModal={showModal}
            title="Warning"
            onCloseFunction={this.handleCloseModal}
            onConfirmFunction={this.handleConfirm}
          />
        )}
        {!isCSVWarning && (
          <Modal show={showModal}>
            <Modal.Body>
              {loading ? 'Processing line. This may take a few moments' : 'Saving changes'}
            </Modal.Body>
          </Modal>
        )}
        <Row>
          <Col xs={12} sm={6}>
            <MapContainerVideoSynchronizer {...mapProps} />
          </Col>
          <Col xs={12} sm={6}>
            <Row>
              <Col xs={12}>
                <ResponsivePlayer
                  attributes={ATTRIBUTES}
                  playbackRate={speed}
                  playerVars={PLAYER_VARS}
                  url={videoOps.getURL(updatedVideo)}
                  onVideoReadyFunction={this.onVideoReady}
                />
                <Styles.DivOnVideo
                  ref={node => this.setReferenceInVideoSynchronizer('nonClickableDiv', node)}
                />
              </Col>
            </Row>
            <ColBreak />
            <Slider {...sliderProps} />
            <InputGroup size="sm">
              <VideoSpeedControls
                isClickableDivHidden={isClickableDivHidden}
                speed={speed}
                speedArray={speedArray}
                changeVideoContainerStateProperty={this.changeVideoSynchronizerStateProperty}
                playPause={this.playPause}
                setReferenceInVideoContainer={this.setReferenceInVideoSynchronizer}
              />
              <InputGroup.Append>
                <Button
                  disabled={speed !== 0 || isEmpty(selectedControlPoint) || isClickableDivHidden}
                  onClick={this.deleteControlPoint}
                >
                  Delete Control Point
                </Button>
              </InputGroup.Append>
              <InputGroup.Append>
                <Button
                  disabled={isEmpty(videoOps.getControlPoints(updatedVideo).length < 3)}
                  size="sm"
                  onClick={this.deleteAllControlPoint}
                  variant="danger"
                >
                  Delete All Control Point
                </Button>
              </InputGroup.Append>
            </InputGroup>
            <ColBreak />
            {showUnlockButton && (
              <Button
                size="sm"
                variant="info"
                active={isClickableDivHidden}
                onClick={this.hideShowClickableDiv}
              >
                {isClickableDivHidden ? 'Lock Carousel' : 'Unlock Carousel'}
              </Button>
            )}
            <CompanyLogo alignRight {...rest} />
          </Col>
        </Row>
      </div>
    );
  }
}
VideoSynchronizer.defaultProps = {};
VideoSynchronizer.propTypes = {
  featureArray: PropTypes.array.isRequired,
  history: PropTypes.object.isRequired,
  ignoreCSVTimes: PropTypes.bool.isRequired,
  isTrackUpdated: PropTypes.bool.isRequired,
  match: PropTypes.object.isRequired,
  project: PropTypes.object.isRequired,
  updatedVideo: PropTypes.object.isRequired,
  changeVideoEditorContainerStateProperty: PropTypes.func.isRequired,
  updateVideoProperty: PropTypes.func.isRequired
};

export default VideoSynchronizer;
